// src/components/Footer.tsx
"use client";

import React, { useMemo, useState } from "react";
import classNames from "classnames";
import { MapPin } from "lucide-react";
import dynamic from "next/dynamic";
import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";

import { useTranslations } from "@/context/TranslationsContext";
import { defaultLanguage } from "@/translations/supportedLanguages";
import logger from "@/utils/logger.client";

const LanguageSwitcher = dynamic(() => import("./LanguageSwitcher"), {
  ssr: false,
});
const PrivacyPolicyModal = dynamic(() => import("./PrivacyPolicyModal"), {
  ssr: false,
});
const TermsConditionsModal = dynamic(() => import("./TermsConditionsModal"), {
  ssr: false,
});

const PAYMENT_ICONS = [
  { src: "/icons/google-pay.svg", alt: "Google Pay" },
  { src: "/icons/apple-pay.svg", alt: "Apple Pay" },
  { src: "/icons/visa.svg", alt: "Visa" },
  { src: "/icons/mastercard.svg", alt: "Mastercard" },
  { src: "/icons/amex.svg", alt: "American Express" },
];

const shouldHideLanguageSwitcher = (pathname: string): boolean => {
  try {
    const normalized = pathname.replace(/^\/[a-z]{2}(\/|$)/, "/");
    return normalized.startsWith("/referrer");
  } catch (err: any) {
    logger.warn(
      "Footer: failed to normalize pathname for language switcher",
      { error: err.message || err }
    );
    return false;
  }
};

const Footer: React.FC = () => {
  const [privacyOpen, setPrivacyOpen] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);
  const { t, direction, language } = useTranslations();
  const pathname = usePathname();
  const year = new Date().getFullYear();

  const { hideLang, isRTL, labels } = useMemo(() => {
    const hideLang = shouldHideLanguageSwitcher(pathname);
    const isRTL = direction === "rtl";
    const labels = {
      address: t("footer.address"),
      privacyPolicy: t("footer.privacyPolicy"),
      terms: t("footer.terms"),
      support: t("footer.customerSupport"),
      copyright: t("footer.copyright"),
    };
    return { hideLang, isRTL, labels };
  }, [pathname, direction, t]);

  return (
    <footer className="bg-[#1F3B4D] text-white py-6 px-4 rounded-lg">
      <div className="container mx-auto flex flex-col items-center space-y-4">
        {!hideLang && <LanguageSwitcher />}

        <div className="flex flex-wrap justify-center items-center gap-4">
          {PAYMENT_ICONS.map(({ src, alt }, idx) => (
            <div
              key={idx}
              className="flex items-center justify-center w-16 h-16 rounded-lg hover:bg-gray-700 transition-colors"
            >
              <Image
                src={src}
                alt={alt}
                width={64}
                height={64}
                className="object-contain w-full h-full"
              />
            </div>
          ))}
        </div>

        {language === defaultLanguage.code && (
          <div
            className={classNames("mt-4 flex flex-col sm:flex-row", {
              "space-y-2 sm:space-y-0 sm:space-x-6": !isRTL,
              "space-y-2 sm:space-y-0 sm:space-x-reverse sm:space-x-6": isRTL,
            })}
          >
            <Link
              href="/referrer/signin"
              className="text-blue-100 hover:text-white text-sm sm:text-base"
            >
              Partner Login
            </Link>
            <Link
              href="/referrer/signup"
              className="text-blue-100 hover:text-white text-sm sm:text-base"
            >
              Become a Partner
            </Link>
          </div>
        )}

        <div
          className={classNames("mt-4 flex items-center", {
            "space-x-2": !isRTL,
            "space-x-reverse space-x-2": isRTL,
          })}
        >
          <MapPin width={16} height={16} aria-hidden="true" />
          <span className="text-gray-200 text-sm sm:text-base">
            {labels.address}
          </span>
        </div>

        <div
          className={classNames("mt-2 flex", {
            "space-x-6": !isRTL,
            "space-x-reverse space-x-6": isRTL,
          })}
        >
          <button
            onClick={() => {
              logger.info("Opening privacy modal");
              setPrivacyOpen(true);
            }}
            className="text-gray-400 hover:text-white focus:ring-2 focus:ring-white rounded"
          >
            {labels.privacyPolicy}
          </button>
          <button
            onClick={() => {
              logger.info("Opening terms modal");
              setTermsOpen(true);
            }}
            className="text-gray-400 hover:text-white focus:ring-2 focus:ring-white rounded"
          >
            {labels.terms}
          </button>
        </div>

        <a
          href="mailto:esim@alodata.net"
          className="mt-4 bg-blue-500 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded transition-colors"
        >
          {labels.support}
        </a>

        <p className="mt-4 text-gray-400 text-sm sm:text-base">
          &copy; {year} {labels.copyright}
        </p>
      </div>

      {privacyOpen && (
        <PrivacyPolicyModal
          isOpen
          onClose={() => {
            logger.info("Closing privacy modal");
            setPrivacyOpen(false);
          }}
        />
      )}

      {termsOpen && (
        <TermsConditionsModal
          isOpen
          onClose={() => {
            logger.info("Closing terms modal");
            setTermsOpen(false);
          }}
        />
      )}
    </footer>
  );
};

export default React.memo(Footer);
